import React, { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Check, X, RefreshCw, Loader2, Moon } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupport,
  isIOSNonStandalone,
  isPreDenied,
  subscribeCurrent,
  autoSubscribeIfReady,
  snoozePush,
  unsnoozePush,
  fetchPushStatus,
} from "@/lib/push";
import { useLang } from "@/contexts/LangContext";
import api from "@/lib/api";

const COPY = {
  en: {
    title: "Notifications",
    checkingCap: "Checking…",
    support: "Browser support",
    permission: "Permission",
    subscribed: "Subscribed",
    granted: "Allowed",
    denied: "Blocked",
    prompt: "Not asked yet",
    unsupported: "Not supported",
    iosInstall: "Install the app first (iOS)",
    fix: "Enable notifications",
    retest: "Send a test to me",
    testFired: "Test fired — check your notifications shade",
    testFailed: "Test failed — check permission",
    yes: "Yes",
    no: "No",
  },
  fr: {
    title: "Notifications",
    checkingCap: "Vérification…",
    support: "Support du navigateur",
    permission: "Permission",
    subscribed: "Abonné",
    granted: "Autorisée",
    denied: "Bloquée",
    prompt: "Pas encore demandée",
    unsupported: "Non pris en charge",
    iosInstall: "Installe l\u2019app d\u2019abord (iOS)",
    fix: "Activer les notifications",
    retest: "M\u2019envoyer un test",
    testFired: "Test envoyé — regarde ta barre de notifications",
    testFailed: "Test échoué — vérifie la permission",
    yes: "Oui",
    no: "Non",
  },
  el: {
    title: "Ειδοποιήσεις",
    checkingCap: "Έλεγχος…",
    support: "Υποστήριξη browser",
    permission: "Άδεια",
    subscribed: "Εγγραφή",
    granted: "Επιτρέπονται",
    denied: "Μπλοκαρισμένες",
    prompt: "Δεν έχει ζητηθεί",
    unsupported: "Δεν υποστηρίζεται",
    iosInstall: "Εγκατέστησε πρώτα την εφαρμογή (iOS)",
    fix: "Ενεργοποίηση ειδοποιήσεων",
    retest: "Στείλε δοκιμή σε εμένα",
    testFired: "Δοκιμή έφυγε — δες τη μπάρα ειδοποιήσεων",
    testFailed: "Η δοκιμή απέτυχε — δες την άδεια",
    yes: "Ναι",
    no: "Όχι",
  },
  pl: {
    title: "Powiadomienia",
    checkingCap: "Sprawdzanie…",
    support: "Obsługa przeglądarki",
    permission: "Zgoda",
    subscribed: "Subskrypcja",
    granted: "Zezwolone",
    denied: "Zablokowane",
    prompt: "Nie pytano",
    unsupported: "Nieobsługiwane",
    iosInstall: "Najpierw zainstaluj aplikację (iOS)",
    fix: "Włącz powiadomienia",
    retest: "Wyślij mi test",
    testFired: "Test wysłany — sprawdź powiadomienia",
    testFailed: "Test nieudany — sprawdź zgodę",
    yes: "Tak",
    no: "Nie",
  },
};

const Row = ({ label, ok, value, compact }) => (
  <div className={"flex items-center justify-between " + (compact ? "text-[11px]" : "text-[12px]")}>
    <span className="text-foreground/60">{label}</span>
    <span className={"inline-flex items-center gap-1 " + (ok ? "text-green-600" : "text-destructive")}>
      {ok ? <Check className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} /> : <X className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />}
      {value}
    </span>
  </div>
);

const NotificationStatusPanel = ({ compact = false }) => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState(null);
  const [expectedPings, setExpectedPings] = useState(null);

  const check = useCallback(async () => {
    if (typeof window === "undefined") return;
    const support = pushSupport();
    let perm = "unsupported";
    if (support) perm = Notification.permission;
    let subscribed = false;
    try {
      if (support && "serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          subscribed = !!sub;
        }
      }
    } catch { /* ignore */ }
    setState({ support, perm, subscribed, ios: isIOSNonStandalone(), preDenied: isPreDenied() });

    // Server-side snooze state — respected by the scheduler even when the
    // browser subscription is technically live.
    try {
      const st = await fetchPushStatus();
      if (st && st.snoozed_until) setSnoozedUntil(st.snoozed_until);
      else setSnoozedUntil(null);
    } catch { /* ignore */ }
  }, []);

  // Load expected ping count so guests know what to expect (transparency).
  useEffect(() => {
    let mounted = true;
    api.get("/push/frequency-public").then((r) => {
      if (mounted) setExpectedPings(Number(r?.data?.random_count ?? 24));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => { check(); }, [check]);

  const onSnooze = async () => {
    setBusy(true);
    try {
      const r = await snoozePush(7);
      if (r.ok) {
        toast.success("Snoozed for a week · Andreas will be back Sunday");
        await check();
      } else {
        toast.error("Couldn't snooze — try again");
      }
    } finally { setBusy(false); }
  };

  const onUnsnooze = async () => {
    setBusy(true);
    try {
      const r = await unsnoozePush();
      if (r.ok) {
        toast.success("Andreas is back");
        setSnoozedUntil(null);
      }
    } finally { setBusy(false); }
  };

  const doFix = async () => {
    if (busy) return;
    // Inside the installed PWA, delegate to the group welcome modal so the
    // guest gets both notifications AND location in one clean flow.
    try {
      const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true;
      if (isStandalone) {
        window.dispatchEvent(new Event("em-open-welcome"));
        return;
      }
    } catch { /* fall through */ }
    setBusy(true);
    try {
      // Try silent auto-subscribe first (works if perm already granted)
      const auto = await autoSubscribeIfReady(lang);
      if (!auto.ok) {
        // Fall back to full ceremony (requests permission)
        await subscribeCurrent(lang);
      }
      await check();
      toast.success("Refreshed");
    } catch (e) {
      const kind = e && e.kind;
      if (kind === "ios-install-required") {
        toast.error(c.iosInstall);
      } else {
        toast.error("Could not enable — check permission in browser settings");
      }
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    // Local test: use the service worker to display a notification without
    // hitting the backend. Proves the SW-notification path works end-to-end.
    if (busy) return;
    setBusy(true);
    try {
      if (Notification.permission !== "granted") {
        toast.error(c.testFailed);
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      if (!reg) throw new Error("no sw");
      await reg.showNotification("Andreas here 👋", {
        body: "Test successful — Cyprus is warming up for you, koumbare.",
        icon: "/icons/icon-192.png",
        badge: "/icons/badge-96.png",
        tag: "andreas-test",
        vibrate: [80, 40, 80],
      });
      toast.success(c.testFired);
    } catch {
      toast.error(c.testFailed);
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const permLabel =
    state.perm === "granted" ? c.granted
    : state.perm === "denied" ? c.denied
    : state.perm === "unsupported" ? c.unsupported
    : c.prompt;

  const supportOk = state.support && !state.ios;
  const permOk = state.perm === "granted";
  const subOk = state.subscribed;
  const allOk = supportOk && permOk && subOk;

  return (
    <div
      className={
        "rounded-lg border border-border bg-background/50 " +
        (compact ? "px-2.5 py-2 space-y-1.5" : "p-3 space-y-2")
      }
      data-testid="notification-status-panel"
    >
      {/* Compact + all-OK: single-line pill so the mobile drawer stays lean */}
      {compact && allOk ? (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="editorial-kicker text-foreground/60 inline-flex items-center gap-2 text-[9px] min-w-0 flex-shrink">
              <Bell className="w-3.5 h-3.5 text-accent shrink-0" />
              <span className="truncate">{c.title}</span>
              {snoozedUntil ? (
                <span className="inline-flex items-center gap-1 text-amber-700 normal-case tracking-normal shrink-0">
                  <Moon className="w-3 h-3" />
                  Snoozed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-green-600 normal-case tracking-normal shrink-0">
                  <Check className="w-3 h-3" />
                  {c.granted}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {snoozedUntil ? (
                <button
                  type="button"
                  onClick={onUnsnooze}
                  disabled={busy}
                  className="text-[10px] tracking-[0.14em] uppercase text-amber-700 hover:text-amber-800 disabled:opacity-60 whitespace-nowrap"
                  data-testid="notif-status-unsnooze"
                >
                  Resume
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onSnooze}
                    disabled={busy}
                    title="Snooze for a week"
                    className="text-[10px] tracking-[0.14em] uppercase text-foreground/50 hover:text-foreground disabled:opacity-60 whitespace-nowrap"
                    data-testid="notif-status-snooze"
                  >
                    Snooze
                  </button>
                  <button
                    type="button"
                    onClick={doTest}
                    disabled={busy}
                    title={c.retest}
                    className="text-[10px] tracking-[0.14em] uppercase text-accent hover:text-accent/80 disabled:opacity-60 whitespace-nowrap"
                    data-testid="notif-status-test"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Test"}
                  </button>
                </>
              )}
            </div>
          </div>
          {expectedPings != null && !snoozedUntil && expectedPings > 0 && (
            <p className="text-[9px] text-foreground/45 mt-0.5 italic">
              ~{Math.max(1, Math.round(expectedPings / 12))}/week from Andreas
            </p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className={"editorial-kicker text-foreground/60 inline-flex items-center gap-2 " + (compact ? "text-[9px]" : "")}>
              {allOk ? <Bell className="w-3.5 h-3.5 text-accent" /> : <BellOff className="w-3.5 h-3.5 text-foreground/40" />}
              {c.title}
            </p>
            <button
              type="button"
              onClick={check}
              className="text-foreground/50 hover:text-foreground p-1 -mr-1"
              aria-label="Refresh"
              data-testid="notif-status-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className={compact ? "space-y-1" : "space-y-1.5"}>
            <Row label={c.support} ok={supportOk} value={supportOk ? c.yes : (state.ios ? c.iosInstall : c.no)} compact={compact} />
            <Row label={c.permission} ok={permOk} value={permLabel} compact={compact} />
            <Row label={c.subscribed} ok={subOk} value={subOk ? c.yes : c.no} compact={compact} />
          </div>
          {!allOk && !state.ios && (
            <button
              type="button"
              onClick={doFix}
              disabled={busy}
              className={
                "w-full inline-flex items-center justify-center gap-2 rounded-lg bg-foreground text-background text-[11px] tracking-[0.16em] uppercase disabled:opacity-60 " +
                (compact ? "h-8 px-3" : "h-10 px-3")
              }
              data-testid="notif-status-fix"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              {c.fix}
            </button>
          )}
          {allOk && !compact && (
            <button
              type="button"
              onClick={doTest}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 h-10 px-3 rounded-lg border border-accent/40 text-accent hover:border-accent hover:bg-accent/10 text-[11px] tracking-[0.16em] uppercase disabled:opacity-60"
              data-testid="notif-status-test"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              {c.retest}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationStatusPanel;
